/**
 * Admin · Profile Insight List
 *
 * Renders the exact ranked insight candidate list the engagement engine builds
 * for a profile when deciding which insight to notify — every scanned video
 * with its score breakdown and a plain-English reason for its place/order (or
 * exclusion). Lets an admin audit the selection algorithm and give feedback.
 *
 * Reached from the Command Center profile row ("Insight list" button) →
 * /admin/operations/profiles/:profileId/insights.
 */
import React from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {Link, useParams} from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {useAdminInsightCandidates} from '../../api/graphql_admin_insights';
import {pageSx, pageTitleSx} from '../../theme/planapusTheme';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

const SEVERITY_COLOR: Record<string, {bg: string; fg: string; border: string}> = {
  serious: {bg: '#fff1f2', fg: '#be123c', border: '#fecdd3'},
  positive: {bg: '#ecfdf5', fg: '#047857', border: '#a7f3d0'},
  mild: {bg: '#fffbeb', fg: '#b45309', border: '#fde68a'},
};

function humanTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 3.6e6) return `${Math.round(diff / 6e4)}m ago`;
  if (diff < 1.728e8) return `${Math.round(diff / 3.6e6)}h ago`;
  return `${Math.round(diff / 8.64e7)}d ago`;
}

type Candidate = NonNullable<
  ReturnType<typeof useAdminInsightCandidates>['data']
>['adminInsightCandidates']['candidates'][number];

function MetricChip({label, value}: {label: string; value: React.ReactNode}): React.JSX.Element {
  return (
    <Box sx={{textAlign: 'center', px: 1.5}}>
      <Typography sx={{fontSize: 20, fontWeight: 800, color: '#0f172a', lineHeight: 1}}>
        {value}
      </Typography>
      <Typography sx={{fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', mt: 0.5}}>
        {label}
      </Typography>
    </Box>
  );
}

function CandidateCard({
  c,
  isChosen,
}: {
  c: Candidate;
  isChosen: boolean;
}): React.JSX.Element {
  const sev = c.severity ? SEVERITY_COLOR[c.severity] : null;
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: isChosen ? '2px solid #2563eb' : '1px solid #e2e8f0',
        bgcolor: c.included ? '#fff' : '#f8fafc',
        opacity: c.included ? 1 : 0.85,
        p: 1.75,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {/* rank badge */}
        <Box
          sx={{
            minWidth: 34,
            height: 34,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 14,
            bgcolor: c.included ? sev?.bg ?? '#eef2ff' : '#e2e8f0',
            color: c.included ? sev?.fg ?? '#3730a3' : '#94a3b8',
            border: `1px solid ${c.included ? sev?.border ?? '#c7d2fe' : '#cbd5e1'}`,
          }}
        >
          {c.included ? `#${c.rankPosition}` : '–'}
        </Box>

        <Box sx={{flex: 1, minWidth: 0}}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 0.25}}>
            <Typography sx={{fontSize: 14, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
              {c.title || '(untitled)'}
            </Typography>
            <Tooltip title="Open video detail">
              <IconButton size="small" component={Link} to={`/admin/content/video/${c.videoId}`} target="_blank">
                <OpenInNewIcon sx={{fontSize: 13}} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography sx={{fontSize: 11, color: '#64748b', mb: 0.75}}>
            {c.channelName || 'unknown channel'} · watched {humanTime(c.lastWatchAt)} · {c.videoId}
          </Typography>

          {/* chips */}
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{mb: 0.75}}>
            {isChosen && (
              <Chip size="small" label="WOULD SEND NOW" sx={{height: 20, fontSize: 10, fontWeight: 800, bgcolor: '#2563eb', color: '#fff'}} />
            )}
            {c.included && c.severity && (
              <Chip size="small" label={`${c.severity} · ${c.tone}`} sx={{height: 20, fontSize: 10, fontWeight: 700, bgcolor: sev?.bg, color: sev?.fg, border: `1px solid ${sev?.border}`}} />
            )}
            <Chip size="small" label={c.aiRating ?? 'unrated'} sx={{height: 20, fontSize: 10, bgcolor: '#f1f5f9', color: '#334155'}} />
            {c.aiConfidence && (
              <Chip size="small" label={`conf ${c.aiConfidence}`} sx={{height: 20, fontSize: 10, bgcolor: '#f1f5f9', color: '#334155'}} />
            )}
            {c.aiScore != null && (
              <Chip size="small" label={`ai ${c.aiScore.toFixed(2)}`} sx={{height: 20, fontSize: 10, bgcolor: '#f1f5f9', color: '#334155'}} />
            )}
            {c.alreadyNotified && (
              <Chip size="small" label="already sent" sx={{height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#f1f5f9', color: '#7c3aed'}} />
            )}
          </Stack>

          {/* score math */}
          {c.included && c.score != null && (
            <Typography sx={{fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#0f172a', mb: 0.5}}>
              score {c.score.toFixed(2)} = {c.scoreBase?.toFixed(2)} base + {c.scoreRecency?.toFixed(2)} recency
              {c.scoreCapped ? '  (raw > 1.00, capped)' : ''}
            </Typography>
          )}

          {/* reason */}
          <Typography sx={{fontSize: 12, color: c.included ? '#334155' : '#94a3b8', lineHeight: 1.5}}>
            {c.reason}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function AdminProfileInsights({client}: Props): React.JSX.Element {
  const {profileId} = useParams<{profileId: string}>();
  const {data, loading, refetch} = useAdminInsightCandidates(client, profileId ?? '');
  const list = data?.adminInsightCandidates;
  const candidates = list?.candidates ?? [];
  const included = candidates.filter(c => c.included);
  const excluded = candidates.filter(c => !c.included);

  return (
    <Box sx={pageSx}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.5}}>
        <IconButton size="small" component={Link} to="/admin/operations/profiles">
          <ArrowBackRoundedIcon fontSize="small" />
        </IconButton>
        <Typography sx={pageTitleSx}>Insight list</Typography>
        <Box sx={{flex: 1}} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => refetch()} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography sx={{fontSize: 13, color: '#64748b', mb: 2}}>
        {list?.profileName ? `${list.profileName} · ` : ''}
        the exact ranked candidate list the engagement engine builds when choosing
        which insight to notify. Order = score (severity tier, then watch recency).
      </Typography>

      {loading && !list ? (
        <Stack alignItems="center" sx={{py: 6}}>
          <CircularProgress size={24} />
        </Stack>
      ) : !list ? (
        <Typography sx={{color: '#94a3b8'}}>No data.</Typography>
      ) : (
        <Stack spacing={2}>
          {/* summary */}
          <Box sx={{borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center'}}>
            <MetricChip label="scanned" value={list.scanned} />
            <Divider orientation="vertical" flexItem />
            <MetricChip label="notify-worthy" value={list.includedCount} />
            <Divider orientation="vertical" flexItem />
            <MetricChip label="excluded" value={list.excludedCount} />
          </Box>
          {list.notes.map((n, i) => (
            <Typography key={i} sx={{fontSize: 12, color: '#64748b', fontStyle: 'italic'}}>
              {n}
            </Typography>
          ))}

          {/* included */}
          <Typography sx={{fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: '#475569', mt: 1}}>
            Notification-worthy ({included.length}) — ranked
          </Typography>
          {included.length === 0 ? (
            <Typography sx={{fontSize: 13, color: '#94a3b8'}}>No notification-worthy insights right now.</Typography>
          ) : (
            included.map(c => (
              <CandidateCard key={c.videoId} c={c} isChosen={c.videoId === list.chosenVideoId} />
            ))
          )}

          {/* excluded */}
          {excluded.length > 0 && (
            <>
              <Typography sx={{fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: '#475569', mt: 1}}>
                Excluded ({excluded.length}) — scanned but not notification-worthy
              </Typography>
              {excluded.map(c => (
                <CandidateCard key={c.videoId} c={c} isChosen={false} />
              ))}
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
